/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { GiftOutlined } from '@ant-design/icons'
import { Checkbox } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  getCandidateName,
  getCandidatePosition,
  getCandidateSubLines,
} from '@sportninja/common/features/playerFinder/candidateDisplay'
import type { PositionNameResolver } from '@sportninja/common/features/playerFinder/candidateDisplay'
import type { SpareCandidate } from '@sportninja/common/features/playerFinder/types'
import { getSkillLevelColor } from '@sportninja/common/features/playerFinder/skillLevelColor'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  resolveAssetUrl,
  type ImageLike,
} from '@sportninja/common/utils/assetUrl'
import React from 'react'
import { font, weightBold, weightRegular } from 'src/components/css'
import { daysLabel, formatRate } from '../utils/candidateFormat'
import GenderIcon from 'src/components/icons/GenderIcon'
import SkillLevelIcon from 'src/components/icons/SkillLevelIcon'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

export interface SpareCandidateCardProps {
  candidate: SpareCandidate
  selected: boolean
  resolvePositionName?: PositionNameResolver
  onToggle: () => void
  onClick: () => void
}

const SpareCandidateCard: React.FC<SpareCandidateCardProps> = ({
  candidate,
  selected,
  resolvePositionName,
  onToggle,
  onClick,
}) => {
  'use memo'
  const avatarUrl =
    resolveAssetUrl(candidate.image as ImageLike) ?? PLAYER_PLACEHOLDER
  const subLines = getCandidateSubLines(candidate, resolvePositionName)
  const ageLine = subLines.find((l) => l.kind === 'age')
  const genderLine = subLines.find((l) => l.kind === 'gender')
  const skillLine = subLines.find((l) => l.kind === 'skill')
  const skillColor = getSkillLevelColor(candidate.skill_level)
  const position = getCandidatePosition(candidate, resolvePositionName)

  // Real API value. A player who hasn't set a rate comes back null/'' — the
  // Rate column is then omitted entirely rather than showing a made-up figure.
  const chargeRate =
    candidate.charge_rate != null && candidate.charge_rate !== ''
      ? candidate.charge_rate
      : undefined
  const hasRate = chargeRate !== undefined
  const days = candidate.available_days ?? []
  const showBottomRow = days.length > 0 || hasRate

  return (
    <div
      css={[cardStyle, selected && cardSelectedStyle]}
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {/* Checkbox stops propagation so ticking never opens the detail modal. */}
      <div
        css={checkboxStyle}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox checked={selected} onChange={onToggle} />
      </div>

      <div css={bodyRowStyle}>
        <img css={avatarStyle} src={avatarUrl} alt='' />
        <div css={leftColStyle}>
          <span css={nameStyle}>{getCandidateName(candidate)}</span>
          {(ageLine || genderLine) && (
            <div css={statsRowStyle}>
              {ageLine && (
                <span css={chipStyle}>
                  <GiftOutlined css={chipIconStyle} />
                  <span css={metaValueStyle}>{ageLine.text}</span>
                </span>
              )}
              {genderLine && (
                <span css={chipStyle}>
                  <GenderIcon size={14} color={colors.PRIMARY} title='Gender' />
                  <span css={metaValueStyle}>{genderLine.text}</span>
                </span>
              )}
            </div>
          )}
          {skillLine && (
            <span css={chipStyle}>
              {/* Icon takes the skill's own colour (green → red ramp), matching
                  mobile, rather than the PRIMARY used by the age/gender chips. */}
              <SkillLevelIcon
                skillLevel={candidate.skill_level}
                size={14}
                // An unrecognised level has no ramp colour; fall back to the
                // body text colour so the glyph still reads.
                color={skillColor ?? colors.WHITE}
              />
              <span
                css={[
                  metaValueStyle,
                  skillColor &&
                    css`
                      color: ${skillColor};
                    `,
                ]}
              >
                {skillLine.text}
              </span>
            </span>
          )}
        </div>
        {!!position && (
          <div css={rightColStyle}>
            <span css={metaTitleStyle}>{capitalizeWords('Position')}</span>
            <span css={metaValueStyle}>{position}</span>
          </div>
        )}
      </div>

      {/* Sits directly beneath the fixed-height identity block above — NOT
          pinned to the card's base, which would let a taller Rate column push
          the divider out of line with a days-only neighbour. */}
      {showBottomRow && (
        <div>
          <div css={dividerStyle} />
          <div css={bottomRowStyle}>
            {days.length > 0 && (
              <div css={bottomItemStyle}>
                <span css={metaTitleStyle}>
                  {capitalizeWords('Available Days')}
                </span>
                <span css={metaValueStyle}>{daysLabel(days)}</span>
              </div>
            )}
            {hasRate && (
              <div css={rateItemStyle}>
                <span css={metaTitleStyle}>{capitalizeWords('Rate')}</span>
                <span css={rateValueStyle}>${formatRate(chargeRate)}</span>
                <span css={rateSuffixStyle}>per game</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SpareCandidateCard

/* The card sits one step LIGHTER than the recessed list behind it
   (SECONDARY_400), so it reads as a raised surface rather than a bordered
   region of the same color. */
const cardStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px;
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 12px;
  background-color: ${colors.SECONDARY_300};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s, transform 0.2s;
  &:hover {
    border-color: ${colors.PRIMARY};
    transform: translateY(-1px);
  }
`

/* Selected cards get a full PRIMARY outline + tint so a ticked player is
   obvious at a glance, not just via the small corner checkbox. */
const cardSelectedStyle = css`
  border-color: ${colors.PRIMARY};
  background-color: ${colors.SECONDARY_200};
`

/* Ant's default unchecked checkbox uses a light-theme border that all but
   disappears on the dark card — it reads as disabled. Brighten the border (and
   give it a subtle fill) so it clearly looks tickable. */
const checkboxStyle = css`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;

  .ant-checkbox-inner {
    background-color: ${colors.SECONDARY_400};
    border-color: ${colors.NEUTRAL_50};
    border-width: 2px;
  }

  &:hover .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${colors.PRIMARY};
  }

  /* Checked state keeps the tenant-aware fill so it reads as "on". */
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${colors.PRIMARY};
    border-color: ${colors.PRIMARY};
  }
`

/* Fixed min-height so the divider below always sits at the SAME offset from the
   top of every card, no matter how many meta lines a candidate has. Sized to
   the tallest identity block: the 3-line name/age-gender/skill stack
   (20 + 2 + 18 + 2 + 18 = 60px), which also clears the 48px avatar. Without
   this, a candidate with no gender or skill pulls its divider upward and the
   cards in a row visibly misalign. Keep in sync with nameStyle/metaValueStyle
   line-heights and leftColStyle's gap. */
const bodyRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-right: 32px;
  min-height: 60px;
`

/* Age + gender chips, mirroring the profile header's stats row
   (ProfilePlayerStatsRow): PRIMARY icon, 4px icon↔text gap, chips kept on one
   line. */
const statsRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const chipStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
`

const chipIconStyle = css`
  color: ${colors.PRIMARY};
  font-size: 14px;
`

const avatarStyle = css`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  background-color: ${colors.SOFT_STEEL};
  flex: 0 0 auto;
`

const leftColStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1.4;
  min-width: 0;
`

const rightColStyle = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`

const nameStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 16px;
  line-height: 20px;
  color: ${colors.WHITE};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const metaTitleStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 14px;
  line-height: 18px;
  color: ${colors.NEUTRAL_50};
`

const metaValueStyle = css`
  ${font.body}
  ${weightRegular}
  font-size: 14px;
  line-height: 18px;
  color: ${colors.WHITE};
`

const dividerStyle = css`
  height: 1px;
  background-color: ${colors.SOFT_STEEL};
  margin: 10px 0;
`

const bottomRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const bottomItemStyle = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`

const rateItemStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`

const rateValueStyle = css`
  ${font.title}
  ${weightBold}
  font-size: 20px;
  line-height: 20px;
  letter-spacing: 1px;
  color: ${colors.SUCCESS_200};
`

const rateSuffixStyle = css`
  ${font.body}
  ${weightRegular}
  font-size: 11px;
  line-height: 14px;
  color: ${colors.NEUTRAL_50};
`
